import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { requestGoogleCalendarSync, requestPortalSync } from "@/services/googleCalendarSync";
import { formatDuration, getTrackedSeconds } from '@/utils/taskTime';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Plus, Grid3X3, List, Kanban, Timer, Clock, BarChart3, Star, AlertTriangle, FileText, User, Calendar, Tag, MapPin, DollarSign, Activity, CheckCircle } from "lucide-react";import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { EnhancedTaskCard } from "@/components/tasks/EnhancedTaskCard";
import { EnhancedTaskForm } from "@/components/tasks/EnhancedTaskForm";
import { TaskFilters, TaskFilters as FilterType } from "@/components/tasks/TaskFilters";
import { TaskKanban } from "@/components/tasks/TaskKanban";
import { TaskGroupedList } from "@/components/tasks/TaskGroupedList";

interface EnhancedTask {
  id: string;
  title: string;
  description?: string;
  status: string;
  priority: string;
  due_date?: string;
  assigned_to?: string;
  created_at: string;
  updated_at?: string;
  case_id?: string;
  task_type?: string;
  category?: string;
  estimated_hours?: number;
  actual_hours?: number;
  hourly_rate?: number;
  is_billable?: boolean;
  show_in_portal?: boolean;
  portal_published_at?: string | null;
  portal_error?: string | null;
  completion_percentage?: number;
  recurring_type?: string;
  recurring_end_date?: string;
  tags?: string[];
  dependencies?: string[];
  milestone?: boolean;
  attachments?: string[];
  notes?: string;
  reminder_date?: string;
  reminder_sent?: boolean;
  color_tag?: string;
  is_private?: boolean;
  deadline_critical?: boolean;
  auto_time_tracking?: boolean;
  time_entries?: any;
  location?: string;
  task_template_id?: string;
  checklist?: any;
  approval_required?: boolean;
  approved_by?: string;
  approved_at?: string;
  collaboration_notes?: any;
  external_links?: any;
  court_deadline?: boolean;
  statute_deadline?: boolean;
  cases?: {
    title: string;
    case_number?: string;
  };
}

const Tasks = () => {
  const [tasks, setTasks] = useState<EnhancedTask[]>([]);
  const [filteredTasks, setFilteredTasks] = useState<EnhancedTask[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<EnhancedTask | null>(null);
  const [selectedTask, setSelectedTask] = useState<EnhancedTask | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [kpiFilter, setKpiFilter] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'kanban'>('list');
  const [viewSection, setViewSection] = useState<'ativas' | 'concluidas'>('ativas');
  // Cronômetros em andamento: id da tarefa -> início (ms). Guardado no navegador para sobreviver a recargas.
  const [timerStarts, setTimerStarts] = useState<Record<string, number>>(() => {
    try {
      return JSON.parse(localStorage.getItem('task_timers') || '{}');
    } catch {
      return {};
    }
  });
  const runningTimers = new Set(Object.keys(timerStarts));
  const saveTimers = (next: Record<string, number>) => {
    setTimerStarts(next);
    try {
      localStorage.setItem('task_timers', JSON.stringify(next));
    } catch {
      // armazenamento indisponível: o cronômetro funciona só nesta sessão
    }
  };
  const [filters, setFilters] = useState<FilterType>({
    search: "",
    dateRange: undefined,
    status: [],
    priority: [],
    taskType: [],
    category: [],
    assignedTo: "",
    caseId: "",
    tags: [],
    billableOnly: false,
    overdueOnly: false,
    criticalOnly: false,
    milestoneOnly: false,
    approvalRequired: false,
    sortBy: "due_date",
    sortOrder: "asc"
  });
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchTasks();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [tasks, filters, viewSection]);

  const fetchTasks = async () => {
    // Tarefas concluídas marcadas para o cliente vão para o Portal do Cliente
    requestPortalSync();
    // Tarefas com prazo também vão para o Google Agenda (celular e tablet)
    requestGoogleCalendarSync();
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Erro",
          description: "Usuário não autenticado",
          variant: "destructive"
        });
        return;
      }

      const { data, error } = await supabase
        .from('tasks')
        .select('*, cases:case_id(id, title, case_number, case_number_cnj)')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro na consulta:', error);
        throw error;
      }
      
      setTasks(data || []);
    } catch (error) {
      console.error('Erro ao buscar tarefas:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as tarefas",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...tasks];

    // Search filter
    if (filters.search) {
      filtered = filtered.filter(task => 
        task.title.toLowerCase().includes(filters.search.toLowerCase()) ||
        task.description?.toLowerCase().includes(filters.search.toLowerCase()) ||
        task.assigned_to?.toLowerCase().includes(filters.search.toLowerCase()) ||
        task.cases?.title.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    // Status filter
    if (filters.status.length > 0) {
      filtered = filtered.filter(task => filters.status.includes(task.status));
    }

    // Priority filter
    if (filters.priority.length > 0) {
      filtered = filtered.filter(task => filters.priority.includes(task.priority));
    }

    // Task type filter
    if (filters.taskType.length > 0) {
      filtered = filtered.filter(task => filters.taskType.includes(task.task_type || 'geral'));
    }

    // Category filter
    if (filters.category.length > 0) {
      filtered = filtered.filter(task => filters.category.includes(task.category || 'geral'));
    }

    // Assigned to filter
    if (filters.assignedTo) {
      filtered = filtered.filter(task => 
        task.assigned_to?.toLowerCase().includes(filters.assignedTo.toLowerCase())
      );
    }

    // Quick filters
    if (filters.billableOnly) {
      filtered = filtered.filter(task => task.is_billable);
    }

    if (filters.overdueOnly) {
      const today = new Date();
      const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      
      filtered = filtered.filter(task => {
        if (!task.due_date || task.status === "Concluída") return false;
        
        const taskDate = new Date(task.due_date);
        const taskDateLocal = new Date(taskDate.getFullYear(), taskDate.getMonth(), taskDate.getDate());
        
        return taskDateLocal.getTime() < todayLocal.getTime();
      });
    }

    if (filters.criticalOnly) {
      filtered = filtered.filter(task => task.deadline_critical);
    }

    if (filters.milestoneOnly) {
      filtered = filtered.filter(task => task.milestone);
    }

    if (filters.approvalRequired) {
      filtered = filtered.filter(task => task.approval_required && !task.approved_by);
    }

    // Separar por seção (Ativas x Concluídas)
    if (viewSection === 'ativas') {
      filtered = filtered.filter(task => task.status !== "Concluída");
    } else {
      filtered = filtered.filter(task => task.status === "Concluída");
    }

    // Sorting principal baseado no critério selecionado
    filtered.sort((a, b) => {
      const aValue = (a as any)[filters.sortBy as keyof EnhancedTask] ?? '';
      const bValue = (b as any)[filters.sortBy as keyof EnhancedTask] ?? '';

      if (filters.sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    // Em "Concluídas", garantir que as mais recentemente concluídas apareçam primeiro
    if (viewSection === 'concluidas') {
      filtered.sort((a, b) => {
        const aDate = new Date(a.updated_at || a.created_at);
        const bDate = new Date(b.updated_at || b.created_at);
        return bDate.getTime() - aDate.getTime();
      });
    }

    setFilteredTasks(filtered);
  };

  const handleFormSubmit = async (formData: any) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Simplificar os dados para salvar
      const dataToSave: any = {
        title: formData.title,
        description: formData.description,
        status: formData.status || 'Pendente',
        priority: formData.priority || 'Média',
        assigned_to: formData.assigned_to || null,
        user_id: user.id
      };

      // Tratar a data de vencimento corretamente
      if (formData.due_date) {
        // Criar data no meio-dia do fuso brasileiro
        const [year, month, day] = formData.due_date.split('-');
        const dueDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0);
        
        // Converter para UTC mantendo o dia correto
        const utcDate = new Date(dueDate.getTime() - (dueDate.getTimezoneOffset() * 60000));
        dataToSave.due_date = utcDate.toISOString();
      }

      // Adicionar case_id apenas se for fornecido
      if (formData.case_id && formData.case_id !== "none") {
        dataToSave.case_id = formData.case_id;
      }

      // Demais campos das abas Detalhes, Faturamento, Checklist e Avançado
      // (antes eram descartados ao salvar)
      const orNull = (value: any) => (value === '' || value === undefined ? null : value);
      const numberOrNull = (value: any) => (value === '' || value === null || value === undefined || isNaN(Number(value)) ? null : Number(value));
      Object.assign(dataToSave, {
        task_type: orNull(formData.task_type),
        category: orNull(formData.category),
        location: orNull(formData.location),
        notes: orNull(formData.notes),
        estimated_hours: numberOrNull(formData.estimated_hours),
        actual_hours: numberOrNull(formData.actual_hours),
        hourly_rate: numberOrNull(formData.hourly_rate),
        is_billable: !!formData.is_billable,
        show_in_portal: !!formData.show_in_portal && !!formData.case_id && formData.case_id !== 'none',
        recurring_type: orNull(formData.recurring_type),
        recurring_end_date: orNull(formData.recurring_end_date),
        reminder_date: orNull(formData.reminder_date),
        tags: formData.tags || [],
        dependencies: formData.dependencies || [],
        attachments: formData.attachments || [],
        checklist: formData.checklist || [],
        milestone: !!formData.milestone,
        is_private: !!formData.is_private,
        deadline_critical: !!formData.deadline_critical,
        auto_time_tracking: !!formData.auto_time_tracking,
        approval_required: !!formData.approval_required,
        court_deadline: !!formData.court_deadline,
        statute_deadline: !!formData.statute_deadline,
      });

      if (editingTask) {
        const { error } = await supabase
          .from('tasks')
          .update(dataToSave)
          .eq('id', editingTask.id);
        
        if (error) throw error;
        toast({ title: "Sucesso", description: "Tarefa atualizada com sucesso" });
      } else {
        const { error } = await supabase
          .from('tasks')
          .insert([dataToSave]);
        
        if (error) throw error;
        toast({ title: "Sucesso", description: "Tarefa criada com sucesso" });
      }

      setIsDialogOpen(false);
      setEditingTask(null);
      fetchTasks();
    } catch (error) {
      console.error('Erro ao salvar tarefa:', error);
      toast({
        title: "Não foi possível salvar a tarefa",
        description: (error as any)?.message || "Tente novamente.",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (task: EnhancedTask) => {
    setEditingTask(task);
    setIsDialogOpen(true);
  };

  const handleView = (task: EnhancedTask) => {
    setSelectedTask(task);
    setIsViewModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir esta tarefa?')) return;

    try {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast({ title: "Sucesso", description: "Tarefa excluída com sucesso" });
      fetchTasks();
    } catch (error) {
      console.error('Erro ao excluir tarefa:', error);
      toast({
        title: "Erro",
        description: "Não foi possível excluir a tarefa",
        variant: "destructive"
      });
    }
  };

  const handleToggleStatus = async (task: EnhancedTask) => {
    const newStatus = task.status === "Concluída" ? "Pendente" : "Concluída";
    
    try {
      const { error } = await supabase
        .from('tasks')
        .update({ 
          status: newStatus,
          completion_percentage: newStatus === "Concluída" ? 100 : task.completion_percentage 
        })
        .eq('id', task.id);

      if (error) throw error;
      fetchTasks();
      toast({
        title: "Sucesso",
        description: `Tarefa marcada como ${newStatus.toLowerCase()}`
      });
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar o status",
        variant: "destructive"
      });
    }
  };

  const handleStartTimer = async (taskId: string) => {
    saveTimers({ ...timerStarts, [taskId]: Date.now() });
    const task = tasks.find(t => t.id === taskId);
    // Começar a trabalhar numa tarefa pendente a coloca em andamento
    if (task && task.status === "Pendente") {
      const { error } = await supabase.from('tasks').update({ status: "Em andamento" }).eq('id', taskId);
      if (!error) setTasks(prev => prev.map(t => (t.id === taskId ? { ...t, status: "Em andamento" } : t)));
    }
    toast({ title: "Cronômetro iniciado", description: "O tempo desta tarefa está sendo contado." });
  };

  const handleStopTimer = async (taskId: string) => {
    const startedAt = timerStarts[taskId];
    const { [taskId]: _removed, ...rest } = timerStarts;
    saveTimers(rest);
    if (!startedAt) return;

    const endedAt = Date.now();
    // Tempo exato da sessão, em segundos (sem arredondar para minutos)
    const seconds = Math.max(0, Math.round((endedAt - startedAt) / 1000));
    const task = tasks.find(t => t.id === taskId);
    const previousSeconds = task ? getTrackedSeconds(task) : 0;
    const totalSeconds = previousSeconds + seconds;
    const actual_hours = Math.round((totalSeconds / 3600) * 10000) / 10000;
    const entries = Array.isArray(task?.time_entries) ? task.time_entries : [];
    const time_entries = [
      ...entries,
      { started_at: new Date(startedAt).toISOString(), ended_at: new Date(endedAt).toISOString(), seconds },
    ];

    const { error } = await supabase.from('tasks').update({ actual_hours, time_entries }).eq('id', taskId);
    if (error) {
      toast({ title: "Erro", description: "Não foi possível salvar o tempo registrado.", variant: "destructive" });
      return;
    }
    setTasks(prev => prev.map(t => (t.id === taskId ? { ...t, actual_hours, time_entries } : t)));
    toast({
      title: `Tempo registrado: ${formatDuration(seconds)}`,
      description: `Total trabalhado nesta tarefa: ${formatDuration(totalSeconds)}.`,
    });
  };

  // Marca/desmarca um item do checklist direto na janela de detalhes
  const toggleChecklistItem = async (task: any, index: number) => {
    const checklist = (Array.isArray(task.checklist) ? task.checklist : []).map((item: any, i: number) =>
      i === index ? { ...item, completed: !item.completed } : item
    );
    setSelectedTask((prev: any) => (prev && prev.id === task.id ? { ...prev, checklist } : prev));
    setTasks(prev => prev.map(t => (t.id === task.id ? { ...t, checklist } : t)));
    const { error } = await supabase.from('tasks').update({ checklist }).eq('id', task.id);
    if (error) {
      toast({ title: "Não foi possível atualizar o checklist", description: error.message, variant: "destructive" });
      fetchTasks();
    }
  };

  const handleTaskDrop = async (taskId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .update({ status: newStatus })
        .eq('id', taskId);

      if (error) throw error;

      toast({
        title: "Sucesso!",
        description: `Tarefa movida para "${newStatus}" com sucesso.`
      });
      fetchTasks(); // Re-fetch to update UI
    } catch (error) {
      console.error("Erro ao mover tarefa:", error);
      toast({
        title: "Erro",
        description: "Não foi possível mover a tarefa.",
        variant: "destructive"
      });
    }
  };



  // Indicadores da página; o selecionado filtra a lista exibida
  const kpiNow = new Date();
  const isTaskOverdue = (t: any) => t.due_date && new Date(t.due_date) < kpiNow && t.status !== "Concluída";
  const taskKpis = [
    { key: 'total', label: 'Total', hint: `${filteredTasks.length} no filtro atual`, list: tasks, tone: 'text-slate-900', pulse: false },
    { key: 'pendentes', label: 'Pendentes', hint: 'aguardando início', list: tasks.filter(t => t.status === "Pendente"), tone: 'text-amber-600', pulse: false },
    { key: 'andamento', label: 'Em andamento', hint: `${runningTimers.size} com cronômetro ativo`, list: tasks.filter(t => t.status === "Em andamento"), tone: 'text-blue-600', pulse: false },
    { key: 'atrasadas', label: 'Atrasadas', hint: 'precisam de atenção', list: tasks.filter(isTaskOverdue), tone: 'text-red-600', pulse: true },
    { key: 'faturaveis', label: 'Faturáveis', hint: 'geram receita', list: tasks.filter(t => t.is_billable), tone: 'text-emerald-600', pulse: false },
  ];
  const activeKpi = taskKpis.find(k => k.key === kpiFilter) || null;
  const displayedTasks = activeKpi ? activeKpi.list : filteredTasks;

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-48 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Sistema de Gestão de Tarefas</h1>
          <p className="text-muted-foreground">Gestão completa de tarefas jurídicas com cronômetro e faturamento</p>
        </div>
        <div className="flex gap-3 items-center">
          {/* Filtro de seção: Ativas x Concluídas */}
          <div className="flex gap-1">
            <Button
              variant={viewSection === 'ativas' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewSection('ativas')}
            >
              Ativas
            </Button>
            <Button
              variant={viewSection === 'concluidas' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewSection('concluidas')}
            >
              Concluídas
            </Button>
          </div>

          {/* Modo de visualização: grade ou lista */}
          <div className="flex gap-1">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('grid')}
            >
              <Grid3X3 className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => setEditingTask(null)}>
                <Plus className="w-4 h-4 mr-2" />
                Nova Tarefa
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto overflow-x-hidden bg-white shadow-2xl border border-[#c4ab83]/30">
              <DialogHeader className="relative -m-6 mb-6 overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] p-6 sm:px-8">
                {/* Monograma AS como marca d'água */}
                <img
                  src="/imgs/logos allana/letra_A_logo.png"
                  alt=""
                  aria-hidden="true"
                  className="pointer-events-none absolute -right-4 top-1/2 h-40 w-40 -translate-y-1/2 select-none object-contain opacity-[0.12]"
                />
                <div className="relative flex items-center gap-4 text-left">
                  <img
                    src="/imgs/logos allana/letra_A_logo.png"
                    alt="Allana Sthéfany"
                    className="h-12 w-12 shrink-0 select-none object-contain opacity-80"
                  />
                  <div className="min-w-0">
                    <DialogTitle className="text-2xl font-semibold tracking-tight text-slate-900">
                      {editingTask ? "Editar Tarefa" : "Nova Tarefa"}
                    </DialogTitle>
                    <p className="mt-1 text-sm text-slate-500">Defina o que precisa ser feito, o prazo e o processo relacionado.</p>
                  </div>
                </div>
              </DialogHeader>
              <EnhancedTaskForm
                task={editingTask}
                onSubmit={handleFormSubmit}
                onCancel={() => setIsDialogOpen(false)}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <TaskFilters
        onFiltersChange={setFilters}
        totalTasks={tasks.length}
        filteredCount={filteredTasks.length}
      />

      {/* Indicadores: clicar filtra a lista logo abaixo */}
      <section className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 sm:grid-cols-5">
        {taskKpis.map((kpi, index) => {
          const active = kpiFilter === kpi.key;
          return (
            <button
              key={kpi.key}
              type="button"
              onClick={() => setKpiFilter(active || kpi.key === 'total' ? null : kpi.key)}
              className={`group relative px-4 py-4 text-left transition-colors duration-200 animate-in fade-in slide-in-from-bottom-1 fill-mode-both last:col-span-2 sm:last:col-span-1 ${
                active ? 'bg-[#faf7f2]' : 'bg-white hover:bg-[#faf7f2]'
              }`}
              style={{ animationDelay: `${index * 40}ms` }}
            >
              {active && <span className="absolute inset-x-0 bottom-0 h-0.5 bg-[#c4ab83]" />}
              <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                {kpi.pulse && kpi.list.length > 0 && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />}
                {kpi.label}
              </div>
              <div className={`mt-1.5 text-2xl font-semibold tabular-nums tracking-tight ${kpi.list.length ? kpi.tone : 'text-slate-900'}`}>{kpi.list.length}</div>
              <div className="mt-0.5 text-[11px] text-slate-400">{kpi.hint}</div>
            </button>
          );
        })}
      </section>

      {activeKpi && (
        <div className="flex flex-wrap items-center gap-2 px-1 text-sm text-slate-500">
          <span>
            Mostrando <strong className="font-semibold text-slate-900">{displayedTasks.length}</strong> tarefa{displayedTasks.length === 1 ? '' : 's'}:
          </span>
          <button
            type="button"
            onClick={() => setKpiFilter(null)}
            className="inline-flex items-center gap-1 rounded-full border border-[#c4ab83]/50 bg-[#faf7f2] px-2.5 py-0.5 text-xs font-medium text-[#7a6440] hover:border-[#c4ab83]"
            title="Remover filtro"
          >
            {activeKpi.label} <span aria-hidden>×</span>
          </button>
        </div>
      )}

      <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as any)}>
        <TabsContent value="grid">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayedTasks.map((task) => (
              <EnhancedTaskCard
                key={task.id}
                task={task}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onView={handleView}
                onToggleStatus={handleToggleStatus}
                onStartTimer={handleStartTimer}
                onStopTimer={handleStopTimer}
                isTimerRunning={runningTimers.has(task.id)}
                timerStartedAt={timerStarts[task.id]}
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="kanban">
          <TaskKanban
            tasks={displayedTasks}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onView={handleView}
            onToggleStatus={handleToggleStatus}
            onStartTimer={handleStartTimer}
            onStopTimer={handleStopTimer}
            runningTimers={runningTimers}
            onTaskDrop={handleTaskDrop}
          />
        </TabsContent>

        <TabsContent value="list">
          <TaskGroupedList
            tasks={displayedTasks}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onView={handleView}
            onToggleStatus={handleToggleStatus}
          />
        </TabsContent>
      </Tabs>

      {displayedTasks.length === 0 && (
        <div className="text-center py-12">
          <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium">Nenhuma tarefa encontrada</h3>
          <p className="text-muted-foreground">
            {filters.search || filters.status.length > 0 ? "Tente ajustar seus filtros" : "Comece criando sua primeira tarefa"}
          </p>
        </div>
      )}

      {/* Modal de Detalhes da Tarefa */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="w-[95vw] max-w-3xl max-h-[85vh] overflow-y-auto overflow-x-hidden bg-white shadow-2xl border border-[#c4ab83]/30">
          <DialogHeader className="relative -m-6 mb-6 overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] p-6 sm:px-8">
            {/* Monograma AS como marca d'água */}
            <img
              src="/imgs/logos allana/letra_A_logo.png"
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute -right-4 top-1/2 h-36 w-36 -translate-y-1/2 select-none object-contain opacity-[0.12]"
            />
            <div className="relative text-left">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Tarefa</p>
              <DialogTitle className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">{selectedTask?.title}</DialogTitle>
              {selectedTask && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
                      selectedTask.status === "Concluída" ? "bg-emerald-50 text-emerald-700" :
                      selectedTask.status === "Em andamento" ? "bg-blue-50 text-blue-700" :
                      selectedTask.status === "Pendente" ? "bg-amber-50 text-amber-700" :
                      "bg-slate-100 text-slate-600"
                    }`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${
                      selectedTask.status === "Concluída" ? "bg-emerald-500" :
                      selectedTask.status === "Em andamento" ? "bg-blue-500" :
                      selectedTask.status === "Pendente" ? "bg-amber-500" :
                      "bg-slate-400"
                    }`} />
                    {selectedTask.status}
                  </span>
                  {selectedTask.priority && (
                    <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
                      selectedTask.priority === "Alta" ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-600"
                    }`}>
                      Prioridade {selectedTask.priority.toLowerCase()}
                    </span>
                  )}
                  {selectedTask.milestone && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-medium text-amber-700">
                      <Star className="h-3 w-3" /> Marco
                    </span>
                  )}
                  {selectedTask.deadline_critical && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-0.5 text-[11px] font-medium text-red-700">
                      <AlertTriangle className="h-3 w-3" /> Prazo crítico
                    </span>
                  )}
                </div>
              )}
            </div>
          </DialogHeader>
          
          {selectedTask && (
            <div className="space-y-6">
              {/* Descrição */}
              {selectedTask.description && (
                <div>
                  <h4 className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 [&>svg]:h-3.5 [&>svg]:w-3.5">
                    <FileText className="w-4 h-4" />
                    Descrição
                  </h4>
                  <p className="text-sm text-slate-800 whitespace-pre-line">{selectedTask.description}</p>
                </div>
              )}

              {/* Checklist */}
              {Array.isArray(selectedTask.checklist) && selectedTask.checklist.length > 0 && (
                <div>
                  <h4 className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                    Checklist ({selectedTask.checklist.filter((i: any) => i.completed).length}/{selectedTask.checklist.length})
                  </h4>
                  <ul className="space-y-1.5">
                    {selectedTask.checklist.map((item: any, index: number) => (
                      <li key={index}>
                        <button
                          type="button"
                          onClick={() => toggleChecklistItem(selectedTask, index)}
                          className="flex w-full items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-left text-sm transition-colors hover:bg-[#faf7f2]"
                        >
                        <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${item.completed ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-300 bg-white'}`}>
                          {item.completed && <CheckCircle className="h-3 w-3" />}
                        </span>
                        <span className={item.completed ? 'text-slate-400 line-through' : 'text-slate-800'}>{item.item}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* informações principais */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {selectedTask.due_date && (
                  <div>
                    <h4 className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 [&>svg]:h-3.5 [&>svg]:w-3.5">
                      <Calendar className="w-4 h-4" />
                      Vencimento
                    </h4>
                    <p className="text-sm text-slate-800">
                      {new Date(selectedTask.due_date).toLocaleDateString('pt-BR')}
                      {new Date(selectedTask.due_date) < new Date() && selectedTask.status !== "Concluída" && (
                        <span className="ml-2 text-destructive text-xs">(Atrasada)</span>
                      )}
                    </p>
                  </div>
                )}
                
                <div>
                  <h4 className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 [&>svg]:h-3.5 [&>svg]:w-3.5">
                    <User className="w-4 h-4" />
                    Atribuído a
                  </h4>
                  <p className="text-sm text-slate-800">{selectedTask.assigned_to || "Não atribuído"}</p>
                </div>

                <div>
                  <h4 className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 [&>svg]:h-3.5 [&>svg]:w-3.5">
                    <Clock className="w-4 h-4" />
                    Criado em
                  </h4>
                  <p className="text-sm text-slate-800">{new Date(selectedTask.created_at).toLocaleDateString('pt-BR')}</p>
                </div>
              </div>

              {/* informações secundárias */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedTask.task_type && (
                  <div>
                    <h4 className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 [&>svg]:h-3.5 [&>svg]:w-3.5">
                      <Tag className="w-4 h-4" />
                      Tipo de Tarefa
                    </h4>
                    <p className="text-sm text-slate-800 capitalize">{selectedTask.task_type}</p>
                  </div>
                )}

                {selectedTask.category && (
                  <div>
                    <h4 className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 [&>svg]:h-3.5 [&>svg]:w-3.5">
                      <Tag className="w-4 h-4" />
                      Categoria
                    </h4>
                    <p className="text-sm text-slate-800 capitalize">{selectedTask.category}</p>
                  </div>
                )}

                {selectedTask.cases?.title && (
                  <div className="md:col-span-2 lg:col-span-3">
                    <h4 className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 [&>svg]:h-3.5 [&>svg]:w-3.5">
                      <FileText className="w-4 h-4" />
                      Processo
                    </h4>
                    <button
                      type="button"
                      onClick={() => navigate(`/processo/${(selectedTask.cases as any).id || selectedTask.case_id}`)}
                      className="group flex w-full items-center justify-between gap-3 rounded-xl border border-[#c4ab83]/30 bg-[#faf7f2] px-3 py-2.5 text-left transition-colors hover:border-[#c4ab83]"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-slate-800">{selectedTask.cases.title}</span>
                        {((selectedTask.cases as any).case_number_cnj || selectedTask.cases.case_number) && (
                          <span className="block font-mono text-xs text-slate-500">{(selectedTask.cases as any).case_number_cnj || selectedTask.cases.case_number}</span>
                        )}
                      </span>
                      <span className="shrink-0 text-xs font-semibold text-[#9a7f52] group-hover:underline">Abrir →</span>
                    </button>
                    {selectedTask.show_in_portal && (
                      <p className={`mt-1.5 text-xs ${selectedTask.portal_published_at ? 'text-emerald-700' : selectedTask.portal_error ? 'text-amber-700' : 'text-slate-500'}`}>
                        {selectedTask.portal_published_at
                          ? `✓ Publicado no Portal do Cliente em ${new Date(selectedTask.portal_published_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`
                          : selectedTask.portal_error
                            ? `Não publicado no portal: ${selectedTask.portal_error}`
                            : 'Vai para o Portal do Cliente quando for concluída.'}
                      </p>
                    )}
                  </div>
                )}

                {selectedTask.location && (
                  <div>
                    <h4 className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 [&>svg]:h-3.5 [&>svg]:w-3.5">
                      <MapPin className="w-4 h-4" />
                      Local
                    </h4>
                    <p className="text-sm text-slate-800">{selectedTask.location}</p>
                  </div>
                )}
              </div>

              {/* informações de faturamento e tempo */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {selectedTask.estimated_hours && (
                  <div>
                    <h4 className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 [&>svg]:h-3.5 [&>svg]:w-3.5">
                      <Clock className="w-4 h-4" />
                      Horas Estimadas
                    </h4>
                    <p className="text-sm text-slate-800">{selectedTask.estimated_hours}h</p>
                  </div>
                )}

                {selectedTask.actual_hours && (
                  <div>
                    <h4 className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 [&>svg]:h-3.5 [&>svg]:w-3.5">
                      <Timer className="w-4 h-4" />
                      Horas Reais
                    </h4>
                    <p className="text-sm text-slate-800">{formatDuration(getTrackedSeconds(selectedTask))}</p>
                  </div>
                )}

                {selectedTask.hourly_rate && selectedTask.is_billable && (
                  <div>
                    <h4 className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 [&>svg]:h-3.5 [&>svg]:w-3.5">
                      <DollarSign className="w-4 h-4" />
                      Valor Hora
                    </h4>
                    <p className="text-sm text-slate-800">
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                      }).format(selectedTask.hourly_rate)}
                    </p>
                  </div>
                )}
              </div>

              {/* Faturamento */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 [&>svg]:h-3.5 [&>svg]:w-3.5">
                    <DollarSign className="w-4 h-4" />
                    Configurações Financeiras
                  </h4>
                  <div className="space-y-1">
                    <p className="text-sm text-slate-800 text-sm">
                      Faturável: {selectedTask.is_billable ? "Sim" : "Não"}
                    </p>
                    {selectedTask.is_billable && selectedTask.hourly_rate && selectedTask.actual_hours && (
                      <p className="text-sm text-slate-800">
                        Valor faturado: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(selectedTask.actual_hours * selectedTask.hourly_rate)}
                      </p>
                    )}
                    {selectedTask.approval_required && (
                      <p className="text-sm text-slate-800 text-sm">
                        Aprovação necessária: {selectedTask.approved_by ? "Sim (Aprovado)" : "Sim (Pendente)"}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Tags e Recorrência */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedTask.tags && selectedTask.tags.length > 0 && (
                  <div>
                    <h4 className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 [&>svg]:h-3.5 [&>svg]:w-3.5">
                      <Tag className="w-4 h-4" />
                      Tags
                    </h4>
                    <div className="flex flex-wrap gap-1">
                      {selectedTask.tags.map((tag, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {selectedTask.recurring_type && (
                  <div>
                    <h4 className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 [&>svg]:h-3.5 [&>svg]:w-3.5">
                      <Clock className="w-4 h-4" />
                      Recorrência
                    </h4>
                    <p className="text-sm text-slate-800 capitalize">{selectedTask.recurring_type}</p>
                    {selectedTask.recurring_end_date && (
                      <p className="text-sm text-muted-foreground">
                        Até: {new Date(selectedTask.recurring_end_date).toLocaleDateString('pt-BR')}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Botões de ação */}
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-4">
                <Button onClick={() => handleDelete(selectedTask.id)} variant="ghost" className="text-slate-500 hover:bg-red-50 hover:text-red-600">
                  Excluir
                </Button>
                <div className="flex gap-2">
                  <Button onClick={() => handleEdit(selectedTask)} variant="outline" className="rounded-xl">
                    Editar
                  </Button>
                  <Button
                    onClick={() => handleToggleStatus(selectedTask)}
                    className={selectedTask.status === "Concluída" ? "rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200" : "rounded-xl bg-slate-900 text-white hover:bg-slate-800"}
                  >
                    {selectedTask.status === "Concluída" ? "Reabrir tarefa" : "Marcar como concluída"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Tasks;